import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import * as fileApi from '@/api/file';
import useFileStore from '@/utils/fileStore';
import { App, Spin, Tooltip } from 'antd';
import pathbe from 'path-browserify-esm';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Editor } from '@aitomol/core';
import { MoleculeEditor } from '@aitomol/react';
import { CalculationFloatButtons, ChargeMultiplicityCard } from './CalculationControls';
import { DocumentPanel } from './DocumentPanel';
import { IsosurfacePanel } from './IsosurfacePanel';
import { VibrationPanel } from './VibrationPanel';
import { createPlatformEditor } from './createPlatformEditor';
import { PlatformPlayback } from './playback';
import { chargeMultiplicityOf, cubeGridOf, initialFrameOf, vibrationsOf } from './types';
import { useEditorSnapshot } from './useEditorSnapshot';
import { fetchVibrationLevels } from './vibration';

/**
 * The platform's 3D molecular GUI pane, built on the aitomol toolkit.
 *
 * Drop-in replacement for `jsmolDisplay`: the same file is fetched through the
 * platform API, the same charge/multiplicity inputs and job launcher sit on top,
 * and the same fullscreen and animation affordances are there. Everything the
 * JSmol iframe could not do - editing, measurements, the selection language,
 * periodic cells, hydrogen bonds, polyhedra, geometry clean-up, undo/redo and
 * real exports - comes from the toolkit's LightCone chrome.
 *
 * The editor is created here (rather than by `<MoleculeEditor>`) so the overlay
 * panels can drive it imperatively; the render backend is left to the toolkit,
 * which owns its lifecycle.
 */
export interface AitomolDisplayProps {
    path?: string;
}

export const AitomolDisplay: React.FC<AitomolDisplayProps> = ({ path }) => {
    const { message } = App.useApp();
    const { t } = useTranslation('common');
    const updateFolder = useFileStore((state) => state.updateFolder);

    const [editor, setEditor] = useState<Editor | null>(null);
    const [playback, setPlayback] = useState<PlatformPlayback | null>(null);
    const [loading, setLoading] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [panelsVisible, setPanelsVisible] = useState(true);
    const [charge, setCharge] = useState(0);
    const [multiplicity, setMultiplicity] = useState(1);
    const [apiLevels, setApiLevels] = useState(0);
    const [orbital, setOrbital] = useState(0);
    const chargeTouched = useRef(false);

    // One editor per mount; StrictMode-safe because creation and disposal are
    // bound to the same effect.
    useEffect(() => {
        const instance = createPlatformEditor();
        const controller = new PlatformPlayback(instance, { fps: 12, loop: true });
        setEditor(instance);
        setPlayback(controller);
        return () => {
            controller.dispose();
            instance.dispose();
            setPlayback(null);
            setEditor(null);
        };
    }, []);

    const state = useEditorSnapshot(editor);
    const structure = state?.structure ?? null;
    const grid = useMemo(() => cubeGridOf(structure), [structure]);
    const fileModes = useMemo(() => vibrationsOf(structure), [structure]);

    // (Re)load the document. `orbital` re-parses a multi-orbital cube so the
    // requested molecular orbital is the one decoded into the grid.
    useEffect(() => {
        if (!editor || !path) return;
        let cancelled = false;
        setLoading(true);
        void (async () => {
            try {
                const content = await fileApi.getRawFileResource(path);
                if (cancelled) return;
                if (content === undefined || content === null) {
                    message.error(t('aitomol.load_failed'));
                    return;
                }
                const result = await editor.load({ data: content, name: pathbe.basename(path), options: { orbital } });
                if (cancelled) return;
                const initialFrame = initialFrameOf(result.structure);
                if (initialFrame !== null && initialFrame > 0) await editor.setFrame(initialFrame);
                if (!chargeTouched.current) {
                    const parsed = chargeMultiplicityOf(result.structure);
                    if (parsed.charge !== null) setCharge(parsed.charge);
                    if (parsed.multiplicity !== null) setMultiplicity(parsed.multiplicity);
                }
                if (result.warnings.length > 0) message.warning(result.warnings.join('; '));
            } catch (error) {
                console.error('[aitomol] load failed', error);
                if (!cancelled) message.error(error instanceof Error ? error.message : t('aitomol.load_failed'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [editor, path, orbital, message, t]);

    // Vibration availability, exactly as the JSmol pane learned it.
    useEffect(() => {
        if (!path) {
            setApiLevels(0);
            return;
        }
        let cancelled = false;
        void fetchVibrationLevels(path).then((levels) => {
            if (!cancelled) setApiLevels(levels);
        });
        return () => {
            cancelled = true;
        };
    }, [path]);

    const onChargeChange = useCallback((value: number) => {
        chargeTouched.current = true;
        setCharge(value);
    }, []);
    const onMultiplicityChange = useCallback((value: number) => {
        chargeTouched.current = true;
        setMultiplicity(value);
    }, []);

    const onSaved = useCallback(() => {
        if (path) void updateFolder(pathbe.dirname(path));
    }, [path, updateFolder]);

    const vibrationActive = playback?.animatingVibration ?? false;
    const frameCount = state?.frameCount ?? 1;
    const animationPlaying = (state?.playing ?? false) && !vibrationActive;

    return (
        <div
            className={`aitomol w-full h-full${isFullScreen ? ' aitomol-fullscreen' : ''}`}
            style={isFullScreen ? { position: 'fixed', inset: 0, height: '100vh', zIndex: 1000 } : { position: 'relative' }}
        >
            {editor ? (
                <MoleculeEditor editor={editor} brand={t('aitomol.brand')} style={{ height: '100%' }} />
            ) : (
                <div className="aitomol-boot">
                    <Spin />
                </div>
            )}

            <div className="aitomol-overlay-stack">
                <div className="aitomol-panel aitomol-panel-toggle">
                    <div className="aitomol-panel-title">
                        <span>{t('aitomol.controls')}</span>
                        <Tooltip
                            title={panelsVisible ? t('aitomol.hide_panels') : t('aitomol.show_panels')}
                            placement="right"
                        >
                            <span
                                className="aitomol-panel-meta aitomol-panel-toggle-btn"
                                role="button"
                                tabIndex={-1}
                                onClick={() => setPanelsVisible((current) => !current)}
                            >
                                {panelsVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                            </span>
                        </Tooltip>
                    </div>
                </div>
                {panelsVisible ? (
                    <>
                        <DocumentPanel editor={editor} structure={structure} path={path} onSaved={onSaved} />
                        <ChargeMultiplicityCard
                            charge={charge}
                            multiplicity={multiplicity}
                            onChargeChange={onChargeChange}
                            onMultiplicityChange={onMultiplicityChange}
                        />
                        {editor && grid ? (
                            <IsosurfacePanel editor={editor} grid={grid} orbital={orbital} onOrbitalChange={setOrbital} />
                        ) : null}
                        {editor && playback ? (
                            <VibrationPanel
                                editor={editor}
                                playback={playback}
                                path={path}
                                structure={structure}
                                fileModes={fileModes}
                                apiLevels={apiLevels}
                            />
                        ) : null}
                    </>
                ) : null}
            </div>

            <CalculationFloatButtons
                path={path}
                charge={charge}
                multiplicity={multiplicity}
                isFullScreen={isFullScreen}
                onToggleFullScreen={() => setIsFullScreen((current) => !current)}
                animationPlaying={animationPlaying}
                animationDisabled={!playback || frameCount <= 1 || vibrationActive}
                onToggleAnimation={() => playback?.toggle()}
            />

            {loading ? (
                <div className="aitomol-loading">
                    <Spin />
                </div>
            ) : null}
        </div>
    );
};

export default AitomolDisplay;